import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { storeQueryKeys } from '../../pages/stores/queryKeys';

/** Toggles whether the household shops at this store. Stops event propagation
 *  so it can sit inside a row that's otherwise a link to the store detail. */
export function FavouriteButton({
  storeId,
  favourite,
  size = 'md',
}: {
  storeId: string;
  favourite: boolean;
  size?: 'sm' | 'md';
}) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (next: boolean) => api.stores.setPrefs(storeId, { favourite: next }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['stores', 'list'] }),
        qc.invalidateQueries({ queryKey: storeQueryKeys.detail(storeId) }),
      ]);
    },
  });

  return (
    <button
      type="button"
      aria-pressed={favourite}
      aria-label={favourite ? 'Remove from favourite stores' : 'Mark as a favourite store'}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle.mutate(!favourite);
      }}
      disabled={toggle.isPending}
      className={`flex items-center justify-center rounded-full border transition-colors ${
        size === 'sm' ? 'size-9 text-lg' : 'size-11 text-xl'
      } ${
        favourite ? 'border-accent bg-accent-soft text-brand' : 'border-line bg-surface text-muted'
      }`}
    >
      {favourite ? '★' : '☆'}
    </button>
  );
}
